import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { site } from '../../data/site.js';
import { useReveal, revealState } from '../../hooks/useReveal.js';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion.js';
import { useIntroLock, shouldRunIntro } from '../../hooks/useIntroLock.js';
import { useTypewriter } from '../../hooks/useTypewriter.js';
import { useHeroLayout } from '../../hooks/useHeroLayout.js';
import { useHeroScrollScale } from '../../hooks/useHeroScrollScale.js';
import { useHeaderRef } from '../../components/layout/headerContext.js';
import { Wrap } from '../../components/ui/Section.jsx';
import styles from './Hero.module.css';

const WORDMARK = 'Abode';
const LETTER_STAGGER_MS = 78;
const LETTER_BASE_DELAY_MS = 150;
/** The letter cascade finishes well before this on every target device. */
const LETTER_FAILSAFE_MS = 1400;
/** The meta line's last span carries a 220ms delay on a 350ms transition. */
const META_FAILSAFE_MS = 800;

/**
 * The home page hero, and the site's signature moment.
 *
 * It choreographs itself on load in four phases, each handing off to the
 * next rather than running on a shared guessed duration:
 *
 *   letters → the wordmark's characters land in sequence
 *   typing  → the tagline types itself in
 *   meta    → the "Est. 2026 / Bloomington" line rises
 *   done    → the scroll lock lifts
 *
 * Every phase also has a failsafe timer, because the lock that holds the
 * page still during the sequence must lift even if a handoff never fires.
 * An unscrollable page is a far worse failure than a skipped animation.
 */
export function Hero() {
  const reducedMotion = usePrefersReducedMotion();
  const headerRef = useHeaderRef();

  const heroRef = useRef(null);
  const wordmarkRef = useRef(null);
  const sloganRef = useRef(null);
  const metaRef = useRef(null);

  // The phase always starts at 'letters', on the server and on the first
  // client render alike. Deciding it up front from shouldRunIntro() instead
  // would make the prerendered markup disagree with hydration for anyone
  // arriving deep-linked, mid-page, or with reduced motion set.
  //
  // Whether the intro should run at all is a question about how the page was
  // arrived at, so it is answered once, in a layout effect — before paint,
  // so a skipped intro never flashes its opening frame.
  const [phase, setPhase] = useState('letters');
  const [runIntro, setRunIntro] = useState(false);

  useLayoutEffect(() => {
    if (shouldRunIntro(reducedMotion)) {
      setRunIntro(true);
    } else {
      setPhase('done');
    }
    // Deliberately mount-only: re-running this mid-visit would replay the
    // intro under someone who has already scrolled past it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [imageRef, imageShown] = useReveal({ mode: 'block' });

  useHeroLayout({ heroRef, wordmarkRef, imageRef, enabled: true });
  useHeroScrollScale({ imageRef, headerRef, enabled: !reducedMotion });
  useIntroLock(runIntro && phase !== 'done', () => setPhase('done'));

  /**
   * Below 640px the slogan is display:none. Typing it there spends ~2.3s
   * animating nothing and holds the meta line — and the intro lock — behind
   * text no one can read.
   */
  const sloganIsVisible = () => sloganRef.current?.offsetParent !== null;

  const startTyping = useCallback(() => {
    setPhase((current) => {
      if (current !== 'letters') return current;
      if (!sloganIsVisible()) return 'meta';

      // Reserve the finished height before the element is emptied. Typed a
      // character at a time it otherwise grows from nothing to three lines
      // and shoves the wordmark below it ~47px down the page as it goes.
      // Measured here, ~1s after load, so the webfont is applied and this
      // is the real wrapped height rather than a fallback-font guess.
      const el = sloganRef.current;
      if (el) el.style.minHeight = `${el.getBoundingClientRect().height}px`;

      return 'typing';
    });
  }, []);

  // Safety net in case the letters' animationend never fires.
  useEffect(() => {
    if (!runIntro || phase !== 'letters') return undefined;
    const timer = setTimeout(startTyping, LETTER_FAILSAFE_MS);
    return () => clearTimeout(timer);
  }, [runIntro, phase, startTyping]);

  const { typed, isTyping, done: typingDone } = useTypewriter(site.tagline, {
    start: phase === 'typing',
  });

  useEffect(() => {
    if (!typingDone) return;
    // Released once the real text is back, so a later resize reflows
    // naturally instead of against a stale floor.
    if (sloganRef.current) sloganRef.current.style.minHeight = '';
    setPhase('meta');
  }, [typingDone]);

  // The meta line is the last thing to move, so the lock lifts when its
  // final span settles rather than on a guessed duration.
  useEffect(() => {
    if (phase !== 'meta') return undefined;
    const last = metaRef.current?.querySelector('span:last-child');
    const settle = () => setPhase('done');
    last?.addEventListener('transitionend', settle, { once: true });
    // Covers the case where transitionend never fires (background tab, etc).
    const timer = setTimeout(settle, META_FAILSAFE_MS);
    return () => {
      clearTimeout(timer);
      last?.removeEventListener('transitionend', settle);
    };
  }, [phase]);

  const metaShown = phase === 'meta' || phase === 'done';
  const sloganText = phase === 'typing' || phase === 'meta' ? typed || site.tagline : site.tagline;

  return (
    <section className={styles.hero} ref={heroRef}>
      <Wrap>
        <div className={styles.top}>
          <p
            ref={sloganRef}
            className={`lede ${styles.slogan}`}
            /* "pending" holds it transparent while its height is still
               being measured; it becomes visible the moment typing starts. */
            data-typed={phase === 'letters' ? 'pending' : 'live'}
          >
            {sloganText}
            {isTyping ? <span className={styles.caret} aria-hidden="true" /> : null}
          </p>
        </div>

        <h1
          ref={wordmarkRef}
          className={`display-huge ${styles.wordmark}`}
          aria-label={WORDMARK}
        >
          {WORDMARK.split('').map((letter, i) => (
            <span
              // eslint-disable-next-line react/no-array-index-key -- "Abode"
              // repeats no letter today, but the index is the stable identity
              // here regardless: position drives the stagger.
              key={`${letter}-${i}`}
              data-letter=""
              aria-hidden="true"
              style={{
                animationDelay: `${LETTER_BASE_DELAY_MS + i * LETTER_STAGGER_MS}ms`,
              }}
              onAnimationEnd={i === WORDMARK.length - 1 ? startTyping : undefined}
            >
              {letter}
            </span>
          ))}
        </h1>

        <div className={styles.meta} ref={metaRef} data-shown={metaShown}>
          <span>Est. {site.founded}</span>
          <span>{site.locationLong}</span>
        </div>

        <div ref={imageRef} className={styles.image} data-reveal={revealState(imageShown)}>
          <img
            src="/images/rendering-01.jpg"
            alt="Modern farmhouse-style custom home built by Abode Custom Homes, featuring board-and-batten cream siding, espresso-brown trim, and a charcoal shingle roof."
            /* The one image above the fold on the site's entry page — it
               should never be lazy, and it is what LCP is measured on. */
            fetchPriority="high"
          />
        </div>
      </Wrap>
    </section>
  );
}
